import { AuthRepository } from "repositories/AuthRepository";
import {
  ClinicRepository,
  UpdateClinicianPayload,
} from "repositories/ClinicRepository";
import { addRequest, removeRequest } from "store/LoadingStore/loadingSlice";
import { AppThunk } from "../rootStore";
import {
  removeCurrentClinician,
  setClinicianPatientsList,
  setClinicianPatientsLoading,
  setClinicianRecoveryTemplates,
  setCliniciansList,
  setClinicStats,
  setClinicTeamLeadsList,
  setClinicTeamLeadsStringList,
  setClinicTeamMembersList,
  setClinicTeamMembersStringList,
  setCurrentClinician,
  setLimit,
  setLimitPatients,
  setLoading,
  setOffset,
  setOffsetPatients,
  setSelectedClinician,
  setSelectedClinicianRoles,
  setSortConfig,
  setSortConfigPatients,
  setTotal,
  setTotalPatients,
  setUpdateAvatarLoading,
} from "./clinicianSlice";
import { Requests } from "../../types/requests";
import { CommonApiParams, FilterDetailsParams } from "types/common";
import { convertFilterParamsToQueryObject } from "utils/formatters";
import { CLINICIAN_PATIENT_LIMIT, CLINICIANS_LIMIT } from "constants/dashboard";
import { Clinician } from "types/Clinician";
import { changeSort, delay } from "utils/general";
import { enqueueSnackbar } from "notistack";

export const initClinicianPage = (): AppThunk => async (dispatch) => {
  try {
    dispatch(setOffset(0));
    dispatch(setOffsetPatients(0));
    dispatch(getCliniciansList());
  } catch (error) {
    console.log(error);
  }
};

export const getCliniciansList: any =
  (callback?: () => void, filters?: any): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.getClinicianList));

      dispatch(getClinicTeamLeads());
      dispatch(getClinicTeamMembers());

      const {
        currentClinician: {
          limit,
          offset,
          sortConfig,
          jointFilterParams,
          search,
        },
      } = getState();

      const filterParamsQueryObject =
        convertFilterParamsToQueryObject(jointFilterParams);

      const apiParams: CommonApiParams = {
        ...filterParamsQueryObject,
        ...(sortConfig.sortBy !== "" && {
          sortBy: sortConfig.sortBy,
          sortDirection: sortConfig.sortDirection,
        }),
        limit,
        offset,
      };
      const clinicRepository = ClinicRepository.getInstance();
      const cliniciansList = await clinicRepository.getCliniciansList({
        ...apiParams,
        search,
        ...filters,
      });
      const items = cliniciansList?.items;

      dispatch(setCliniciansList(items));
      dispatch(setTotal(cliniciansList?.totalCount));
      dispatch(setLimit(CLINICIANS_LIMIT));
    } catch (error) {
      console.log("Error from getCliniciansList Thunk", error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getClinicianList));

      if (callback) callback();
    }
  };

export const getClinicianPatientStats: any =
  (
    clinicId: string,
    params: {
      startDate: Date;
      endDate: Date;
      sort: string;
      hideNoPatient: boolean;
    },
    callback?: (activePatients: number) => void
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.getClinicPatientsList));

      const clinicRepository = ClinicRepository.getInstance();
      const statsList = await clinicRepository.getClinicianPatientStats(
        params.startDate,
        params.endDate,
        clinicId,
        params.sort
      );
      const result = params.hideNoPatient
        ? statsList?.filter((item: any) => item.patients > 0)
        : statsList;
      dispatch(setClinicStats(result));
      if (callback) callback(statsList?.activePatients);
    } catch (error) {
      console.log("Error from getCliniciansList Thunk", error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getClinicianPatientStats));
    }
  };

export const loadMoreClinicians =
  (page: number): AppThunk =>
  async (dispatch, getState) => {
    try {
      const clinicRepository = ClinicRepository.getInstance();
      const {
        currentClinician: { limit, sortConfig, search },
      } = getState();

      dispatch(addRequest(Requests.loadMoreClinicians));
      const newOffset = (page - 1) * limit;
      const apiParams: CommonApiParams = {
        ...(sortConfig.sortBy !== "" && {
          sortBy: sortConfig.sortBy,
          sortDirection: sortConfig.sortDirection,
        }),
        limit,
        offset: (page - 1) * limit,
      };

      const clinicians = await clinicRepository.getCliniciansList({
        ...apiParams,
        search,
      });
      dispatch(setCliniciansList(clinicians.items));
      dispatch(setOffset(newOffset));
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(removeRequest(Requests.loadMoreClinicians));
    }
  };

export const getCurrentClinician: any =
  (callback?: () => void): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.getCurrentClinician));

      const authRepository = AuthRepository.getInstance();
      const currentClinician = await authRepository.getMe();
      dispatch(setCurrentClinician(currentClinician));
    } catch (error) {
      console.log("Error from getCurrentClinician Thunk", error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getCurrentClinician));

      if (callback) callback();
    }
  };

export const getSelectedClinician: any =
  (clinicianId: string, callback?: () => void): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.getSelectedClinician));

      const clinicianRepository = ClinicRepository.getInstance();
      const selectedClinician = await clinicianRepository.getSelectedClinician(
        clinicianId
      );
      dispatch(setSelectedClinician(selectedClinician));
      dispatch(setSelectedClinicianRoles(selectedClinician?.role ?? []));
    } catch (error) {
      console.log("Error from getSelectedClinician Thunk", error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getSelectedClinician));

      if (callback) callback();
    }
  };

export const updateClinician =
  (
    clinicianId: string,
    payload: UpdateClinicianPayload,
    callback?: () => void
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.updateCurrentClinician));

      const clinicianRepository = ClinicRepository.getInstance();
      await clinicianRepository.updateCurrentClinician(clinicianId, payload);
      const authRepository = AuthRepository.getInstance();
      const currentClinician = await authRepository.getMe();
      dispatch(setCurrentClinician(currentClinician));
      enqueueSnackbar("Changes saved successfully!.", { variant: "success" });
    } catch (error: any) {
      enqueueSnackbar(
        "Something went wrong. Please try again later. If the error persists, contact us for support.",
        { variant: "error" }
      );
      console.log("Error from updateCurrentClinician Thunk", error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.updateCurrentClinician));

      if (callback) callback();
    }
  };

export const updateCurrentClinicianAvatar =
  (payload: FormData, callback?: () => void, onError?: () => void): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setUpdateAvatarLoading(true));
      dispatch(addRequest(Requests.updateCurrentClinicianAvatar));
      const clinicianRepository = ClinicRepository.getInstance();
      const currentClinician = await clinicianRepository.updateClinicianAvatar(
        payload
      );
      dispatch(setCurrentClinician(currentClinician));
    } catch (error) {
      console.log("Error from updateCurrentClinicianAvatar Thunk", error);
      if (onError) onError();
    } finally {
      dispatch(setUpdateAvatarLoading(false));
      dispatch(removeRequest(Requests.updateCurrentClinicianAvatar));

      if (callback) callback();
    }
  };

export const clearCurrentClinician =
  (callback?: () => void): AppThunk =>
  (dispatch) => {
    try {
      dispatch(removeCurrentClinician());
    } catch (error) {
      console.log("Error from clearCurrentClinician Thunk", error);
    } finally {
      if (callback) callback();
    }
  };

export const deleteClinician =
  (clinicianId: string, callback?: () => void): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.deleteClinician));
      const clinicianRepository = ClinicRepository.getInstance();

      await clinicianRepository.disableClinicianFromCognito(clinicianId);
    } catch (error) {
      console.log("Error from updateCurrentClinician Thunk", error);
    } finally {
      if (callback) callback();
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.deleteClinician));
    }
  };

export const editClinicianCognitoGroups =
  (
    clinicianId: string,
    groupsToDelete: string[],
    groupsToAdd: string[],
    clinicianEmail: string,
    addUserToGroup: (username: string, groupName: string) => void
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.editClinicianCognitoGroups));
      const clinicianRepository = ClinicRepository.getInstance();

      for (let i = 0; i < groupsToDelete.length; i++) {
        const group = groupsToDelete[i];
        await clinicianRepository.removeClinicianFromCognitoGroup(
          clinicianId,
          group
        );
      }

      for (let i = 0; i < groupsToAdd.length; i++) {
        const group = groupsToAdd[i];
        await addUserToGroup(clinicianEmail, group);
      }
    } catch (error) {
      console.log("Error from updateCurrentClinician Thunk", error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.editClinicianCognitoGroups));
    }
  };

export const getClinicianPatientsList: any =
  (
    clinicId: string,
    callback?: () => void,
    searchingFor?: string,
    detailedFilter?: FilterDetailsParams
  ): AppThunk =>
  async (dispatch, getState) => {
    try {
      dispatch(setClinicianPatientsLoading(true));
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.getClinicianPatients));

      const {
        currentClinician: {
          limitPatients,
          offsetPatients,
          sortConfigPatients,
          jointFilterParamsPatients,
        },
      } = getState();

      const filterParamsQueryObject = convertFilterParamsToQueryObject(
        jointFilterParamsPatients
      );

      const apiParams: CommonApiParams = {
        ...filterParamsQueryObject,
        ...(sortConfigPatients.sortBy !== "" && {
          sortBy: sortConfigPatients.sortBy,
          sortDirection: sortConfigPatients.sortDirection,
        }),
        limit: limitPatients,
        offset: offsetPatients,
      };
      const clinicRepository = ClinicRepository.getInstance();
      const clinicianPatientsList = await clinicRepository.getClinicianPatients(
        clinicId,
        apiParams,
        searchingFor,
        detailedFilter
      );
      const items = clinicianPatientsList?.items;
      dispatch(setClinicianPatientsList(items));
      dispatch(setTotalPatients(clinicianPatientsList?.totalCount));
      dispatch(setLimitPatients(CLINICIAN_PATIENT_LIMIT));
    } catch (error) {
      console.log("Error from getClinicianPatients Thunk", error);
    } finally {
      dispatch(setLoading(false));
      dispatch(setClinicianPatientsLoading(false));

      dispatch(removeRequest(Requests.getClinicianPatients));

      if (callback) callback();
    }
  };

export const getClinicRecoveryTemplates: any =
  (clinicId: string, callback?: () => void): AppThunk =>
  async (dispatch) => {
    try {
      const clinicRepository = ClinicRepository.getInstance();
      const clinicRecoveryPlanTemplates =
        await clinicRepository.getClinicRecoveryTemplates(clinicId);
      const items = clinicRecoveryPlanTemplates;
      dispatch(setClinicianRecoveryTemplates(items));
    } catch (error) {
      console.log("Error from getClinicRecoveryTemplates Thunk", error);
    } finally {
      dispatch(removeRequest(Requests.getClinicianRecoveryTemplates));

      if (callback) callback();
    }
  };
export const loadMoreClinicianPatients =
  (clinicianId: string): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setLoading(true));
    try {
      const clinicRepository = ClinicRepository.getInstance();
      const {
        currentClinician: {
          limitPatients,
          offsetPatients,
          clinicianPatientsList,
          sortConfigPatients,
          totalPatients,
        },
      } = getState();
      if (totalPatients <= offsetPatients + limitPatients) {
        return;
      }
      dispatch(addRequest(Requests.loadMoreClinicianPatients));
      const newOffset = offsetPatients + limitPatients;
      const apiParams: CommonApiParams = {
        ...(sortConfigPatients.sortBy !== "" && {
          sortBy: sortConfigPatients.sortBy,
          sortDirection: sortConfigPatients.sortDirection,
        }),
        limit: limitPatients,
        offset: newOffset,
      };

      const clinicianPatients = await clinicRepository.getClinicianPatients(
        clinicianId,
        apiParams
      );
      const items = clinicianPatients.items;
      const totalItems = [...(clinicianPatientsList ?? []), ...items];
      dispatch(setClinicianPatientsList(totalItems));
      dispatch(setOffsetPatients(newOffset));
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.loadMoreClinicianPatients));
    }
  };

export const getClinicTeamLeads: any =
  (callback?: () => void): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.getClinicTeamLeads));
      const clinicianRepository = ClinicRepository.getInstance();
      const clinicTeamLeads =
        await clinicianRepository.getClinicTeamLeadsList();

      const teamLeads = clinicTeamLeads.items;

      const teamLeadsString = teamLeads.map(
        (e: Clinician) =>
          e.profileInfo?.firstName + " " + e.profileInfo?.lastName
      );
      dispatch(setClinicTeamLeadsList(teamLeads));
      dispatch(setClinicTeamLeadsStringList(teamLeadsString));
    } catch (error) {
      console.log("Error from getClinicTeamLeads Thunk", error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getClinicTeamLeads));
      if (callback) callback();
    }
  };

export const getClinicTeamMembers: any =
  (callback?: () => void): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoading(true));
      dispatch(addRequest(Requests.getClinicTeamMembers));
      const clinicianRepository = ClinicRepository.getInstance();
      const clinicTeamMembers =
        await clinicianRepository.getClinicTeamMembersList();
      const teamMembers = clinicTeamMembers.items;
      const teamMembersString = teamMembers.map(
        (e: Clinician) =>
          e.profileInfo?.firstName + " " + e.profileInfo?.lastName
      );
      dispatch(setClinicTeamMembersList(teamMembers));
      dispatch(setClinicTeamMembersStringList(teamMembersString));
    } catch (error) {
      console.log("Error from getClinicTeamMembers Thunk", error);
    } finally {
      dispatch(setLoading(false));
      dispatch(removeRequest(Requests.getClinicTeamMembers));
      if (callback) callback();
    }
  };

export const changeSortOfClinicList =
  (sortBy: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      const {
        currentClinician: { sortConfig },
      } = getState();

      const changedSortConfig = changeSort(sortBy, sortConfig);
      dispatch(setSortConfig(changedSortConfig));
      dispatch(setOffset(0));

      dispatch(getCliniciansList());
    } catch (error) {
      console.log(error);
    }
  };

export const changeSortOfClinicianPatientsList =
  (sortBy: string, clinicianId: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      const {
        currentClinician: { sortConfigPatients },
      } = getState();
      const changedSortConfig = changeSort(sortBy, sortConfigPatients);
      dispatch(setSortConfigPatients(changedSortConfig));
      dispatch(setOffsetPatients(0));

      dispatch(getClinicianPatientsList(clinicianId));
    } catch (error) {
      console.log(error);
    }
  };
